import { Router } from '@angular/router';
import { RecentlyViewedService } from '@df/catalog-ui/recently-viewed/recently-viewed.service';
import { Category, ICategoryDataV3 } from '@df/catalog/category/category';
import { Model } from '@df/catalog/model';
import { IProductDataV3, Product } from '@df/catalog/product/product';
import { ProductResource } from '@df/catalog/product/product.resource';
import { Meta } from '@df/core/meta/meta';
import { KlevuSearch } from '@df/module-klevu-search/klevu-interfaces';
import { KlevuSearchService } from '@df/module-klevu-search/klevu-search.service';
import { NgInject } from '@df/ng/ng-inject';
import { Basket } from '@df/session/api/basket/basket';
import compact from 'lodash-es/compact';
import take from 'lodash-es/take';
import uniq from 'lodash-es/uniq';
import { ModelCarouselEngine } from '../../catalog/page/component/model-carousel/model-carousel-engine';
import { ModelCarouselService } from '../../catalog/page/component/model-carousel/model-carousel.service';

export class KlevuContextRecommendationsModelCarouselEngine extends ModelCarouselEngine<Product> {
    @NgInject(KlevuSearchService)
    private klevuSearchService!: KlevuSearchService;
    @NgInject(Meta)
    private meta!: Meta;
    @NgInject(ProductResource)
    productResource!: ProductResource;
    @NgInject(RecentlyViewedService)
    recentlyViewedService!: RecentlyViewedService;
    @NgInject(Router)
    router!: Router;
    @NgInject(Basket)
    basket!: Basket;

    override isModelRelative = true;

    private cache: { [context: string]: number[] } = {};

    get(type: string, data: KlevuSearch.IContextRecommendationsModelCarouselLoadRequest): Promise<Product[]> {
        const model = this.meta.model;
        const cKey = [
            model?.type || this.router.url,
            model?.id || this.router.url,
            data.strategyId,
            data.isRecentlyViewed
                ? this.recentlyViewedService
                      .getItems()
                      .map(item => item.id)
                      .join('-')
                : ''
        ].join();
        return new Promise((resolve, reject) => {
            const resolveIds = (ids: number[]) =>
            !!ids ?
                this.productResource
                    .loadAllIds(ids, 2, { safe: true })
                    .then(products => resolve(data.limit ? take(products, data.limit) : products)) : resolve([]);
            if (!this.cache[cKey] && this.meta.model) {
                this.load(this.meta.model, data)
                    .then(result => {
                        this.cache[cKey] = result;
                        resolveIds(this.cache[cKey]);
                    })
                    .catch(() => resolve([]));
            } else {
                resolveIds(this.cache[cKey]);
            }
        });
    }

    private load(model: Model, engineOptions: KlevuSearch.IContextRecommendationsOptions): Promise<number[]> {
        const strategyId = engineOptions?.strategyId;
        if (strategyId) {
            if (model && model.type !== 'page') {
                if ((<Model>model).type === 'product') {
                    return this.klevuSearchService.loadContextRecommendationsForProduct(
                        strategyId,
                        this.klevuSearchService.getKlevuProductId(model as Product<IProductDataV3>),
                        (model as Product<IProductDataV3> | undefined)?.data?.parent,
                        engineOptions.isRecentlyViewed
                    );
                }

                if ((<Model>model).type === 'category') {
                    return this.klevuSearchService.loadContextRecommendationsForCategory(
                        strategyId,
                        this.klevuSearchService.getCategoryPath(model as Category<ICategoryDataV3>),
                        engineOptions.isRecentlyViewed
                    );
                }
            } else {
                return this.klevuSearchService.loadContextRecommendationsForBasket(
                    strategyId,
                    compact(uniq(this.basket.items.map(item => item.product && this.klevuSearchService.getKlevuProductId(item.product)))),
                    engineOptions.isRecentlyViewed
                );
            }
        }
        return new Promise(resolve => resolve([]));
    }
}
ModelCarouselService.engines[KlevuSearch.contextRecommendationsEngineId] = new KlevuContextRecommendationsModelCarouselEngine();
